import { useCallback, useEffect, useId, useState } from 'react';
import type { FC, ReactNode } from 'react';
import clsx from 'clsx';
import { Drawer } from '@app/components/drawer';
import { useCollapsedDocumentLayout } from '@app/components/document-layout-container/collapsed-document-layout/context';
import SingleMessageBox from '@app/components/messages-layout/single-message-box';
import { T } from '@app/components/translation';

import { useAnnotationProps } from '@app/contexts/annotation-props';
import useResetFocusedEditor from '@app/hooks/rich-text-editor/use-reset-focused-editor';
import useCurrentContractId from '@app/hooks/use-current-contract-id';

import { isComment, isSuggestion } from '@app/utils/comments';

import type { Annotation } from '@app/utils/comments/types/types';

import { isSmallScreenWidth } from '@flow/libs/ui/config';

import SingleComment from './single-comment';

import style from './annotation.module.scss';

type Props = {
  children: ReactNode;
  isOpen?: boolean;
  className?: string;
  annotation: Annotation;
  isReadOnly?: boolean;
  onClosePopover?: () => void;
};

const AnnotationWrapper: FC<Props> = ({
  children,
  isOpen = false,
  className,
  annotation,
  isReadOnly,
  onClosePopover,
}) => {
  const resetFocusedEditor = useResetFocusedEditor();
  const isSmallScreen = isSmallScreenWidth();
  const contractId = useCurrentContractId() as number;
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const { setIsRecipientSelectorOpen } = useCollapsedDocumentLayout();
  const {
    acceptedSuggestion,
    hoveredAnnotationId,
    newAnnotationIdRef,
    openAnnotationId,
    setHoveredAnnotationId,
    setOpenAnnotationId,
  } = useAnnotationProps();
  const { isAcceptedSuggestion, messageId } = acceptedSuggestion;
  const annotationId: number | null = annotation.id ?? annotation._id ?? null;
  const isCurrentAnnotationOpen = openAnnotationId === annotationId;
  const isCurrentAnnotationHovered = hoveredAnnotationId === annotationId;
  const uniqueId = useId();

  useEffect(() => {
    setTimeout(() => {
      const newIsPopoverOpen =
        isOpen && !isReadOnly && newAnnotationIdRef?.current !== annotationId;

      setIsPopoverOpen(newIsPopoverOpen);
      setOpenAnnotationId(newIsPopoverOpen ? annotationId : null);
    }, 0);
  }, [
    annotationId,
    isOpen,
    isReadOnly,
    newAnnotationIdRef,
    setOpenAnnotationId,
  ]);

  const onClick = useCallback(() => {
    if (isPopoverOpen) {
      return;
    }

    setIsPopoverOpen(true);
    setOpenAnnotationId(annotationId);
  }, [
    annotationId,
    isPopoverOpen,
    setOpenAnnotationId,
  ]);

  const onClose = useCallback(() => {
    setIsPopoverOpen(false);
    setIsRecipientSelectorOpen(false);
    setOpenAnnotationId(null);

    if (onClosePopover) {
      onClosePopover();
    }
  }, [
    onClosePopover,
    setIsRecipientSelectorOpen,
    setOpenAnnotationId,
  ]);

  const shouldHighlightAnnotation = isCurrentAnnotationHovered || isCurrentAnnotationOpen;
  const annotationClassName = clsx(className, 'annotation', {
    [style.AcceptSuggestionAnimation]: annotationId === messageId && isAcceptedSuggestion,
    [style.AnimateComment]: isComment(annotation) && newAnnotationIdRef?.current === annotationId,
    [style.AnimateSuggestion]:
      isSuggestion(annotation) && newAnnotationIdRef?.current === annotationId,
    [style.HighlightedComment]: shouldHighlightAnnotation && isComment(annotation),
    [style.HighlightedSuggestion]: shouldHighlightAnnotation && isSuggestion(annotation),
    [style.SuggestionAnnotation]: isSuggestion(annotation),
  });

  if (isSmallScreen) {
    return (
      <>
        <Drawer
          onOpenChange={(open) => open && resetFocusedEditor()}
          onClose={onClose}
          id={uniqueId}
          isDragHandleVisible
          isOverlayEnabled
          renderTitle={() => (
            <>
              {isSuggestion(annotation) && (
                <T
                  _str="Suggestion"
                  _comment="Text to show this message is a suggestion"
                />
              )}
              {isComment(annotation) && (
                <T
                  _str="Comment"
                  _comment="Text to show this message is an inline comment"
                />
              )}
            </>
          )}
          renderTrigger={() => (
            <span
              id={`annotation-${annotationId}`}
              aria-hidden
              className={annotationClassName}
              role="button"
              tabIndex={isReadOnly ? 0 : undefined}
            >
              {children}
            </span>
          )}
        >
          <SingleMessageBox
            agreementId={contractId}
            message={annotation}
            amplitudeData={{
              location: 'contract view - popover',
            }}
            isMobileView={isSmallScreen}
          />
        </Drawer>
      </>
    );
  }

  return (
    <SingleComment
      annotation={annotation}
      isOpen={isPopoverOpen}
      onEscapeKeyDown={onClose}
      onInteractOutside={onClose}
    >
      <span
        aria-hidden
        className={annotationClassName}
        id={`annotation-${annotationId}`}
        onClick={onClick}
        onMouseEnter={() => setHoveredAnnotationId(annotationId)}
        onMouseLeave={() => setHoveredAnnotationId(null)}
        role="button"
        tabIndex={isReadOnly ? 0 : undefined}
      >
        {children}
      </span>
    </SingleComment>
  );
};

export default AnnotationWrapper;
